import { ReactElement, useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import type { DropResult } from '@hello-pangea/dnd'
import { DragAndDropIcon, EditIcon, RemoveIcon } from 'src/v3/core/icons'
import { isFuzzyMatch } from 'src/v3/utilities/fuzzy-match'
import { paperInputSx } from '../../constants'
import { InstructorWithCourses } from '../types'

type InstructorsSectionProps = {
  courseInstructors: InstructorWithCourses[]
  availableInstructors: InstructorWithCourses[]
  isSaving: boolean
  onAdd: (instructorID: string) => void
  onCreateNew: (name: string) => void
  onEdit: (instructor: InstructorWithCourses) => void
  onRemove: (instructor: InstructorWithCourses) => void
  onReorder: (reordered: InstructorWithCourses[]) => void
}

type InstructorOption =
  | InstructorWithCourses
  | {
      id: '__create__'
      name: string
      inputValue: string
      linkedServiceID: string
      credentials?: string
      courseAssociations: []
    }

export default function InstructorsSection({
  courseInstructors,
  availableInstructors,
  isSaving,
  onAdd,
  onCreateNew,
  onEdit,
  onRemove,
  onReorder,
}: InstructorsSectionProps): ReactElement {
  let [localOrder, setLocalOrder] =
    useState<InstructorWithCourses[]>(courseInstructors)

  useEffect(() => {
    if (!isSaving) setLocalOrder(courseInstructors)
  }, [courseInstructors, isSaving])

  let handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    let src = result.source.index
    let dst = result.destination.index
    let moved = localOrder[src]
    let reordered = localOrder
      .filter((_, i) => i !== src)
      .toSpliced(dst, 0, moved)
    setLocalOrder(reordered)
    onReorder(reordered)
  }

  return (
    <Box>
      <Autocomplete<InstructorOption>
        options={availableInstructors}
        value={null}
        disabled={isSaving}
        getOptionLabel={(option) =>
          option.id === '__create__'
            ? option.name
            : option.credentials
              ? `${option.name} — ${option.credentials}`
              : option.name
        }
        filterOptions={(options, params) => {
          let q = params.inputValue
          let filtered = options.filter(
            (o) =>
              isFuzzyMatch(o.name, q) ||
              (o.credentials ? isFuzzyMatch(o.credentials, q) : false),
          )
          let exactMatch = options.some(
            (o) => o.name.toLowerCase() === q.toLowerCase(),
          )
          let createOption =
            q.trim() && !exactMatch
              ? [
                  {
                    id: '__create__',
                    name: `Create "${q}"`,
                    inputValue: q,
                    linkedServiceID: '',
                    courseAssociations: [] as [],
                  },
                ]
              : []
          return [...filtered, ...createOption]
        }}
        onChange={(_, option) => {
          if (!option) return
          if (option.id === '__create__' && 'inputValue' in option) {
            onCreateNew(option.inputValue)
          } else {
            onAdd(option.id)
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add Instructor"
            placeholder="Search by name…"
            sx={paperInputSx}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.id === '__create__' ? (
              <Typography sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                {option.name}
              </Typography>
            ) : (
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                {option.credentials && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {option.credentials}
                  </Typography>
                )}
              </Box>
            )}
          </li>
        )}
        sx={{ mb: 2 }}
      />

      <Box aria-live="polite" aria-atomic="true" className="sr-only">
        {isSaving ? 'Saving…' : ''}
      </Box>
      {isSaving && (
        <Box className="flex items-center gap-2 mb-2">
          <CircularProgress size={14} aria-label="Saving" />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Saving…
          </Typography>
        </Box>
      )}

      {localOrder.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No instructors assigned to this course yet.
        </Typography>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="instructors">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {localOrder.map((instructor, index) => (
                <Draggable
                  key={instructor.id}
                  draggableId={instructor.id}
                  index={index}
                  isDragDisabled={isSaving}
                >
                  {(prov) => (
                    <Box
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      className="flex items-center gap-2 py-1"
                    >
                      <Box
                        {...prov.dragHandleProps}
                        aria-label={`Drag to reorder ${instructor.name}`}
                        className="flex items-center"
                        sx={{
                          cursor: isSaving ? 'default' : 'grab',
                          color: 'text.secondary',
                        }}
                      >
                        <DragAndDropIcon fontSize="small" />
                      </Box>
                      <Box className="flex-1 min-w-0">
                        <Typography variant="body2" noWrap>
                          {instructor.name}
                        </Typography>
                        {instructor.credentials && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                            noWrap
                          >
                            {instructor.credentials}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title="Edit instructor">
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={`Edit ${instructor.name}`}
                          onClick={() => onEdit(instructor)}
                          disabled={isSaving}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove from course">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Remove ${instructor.name} from course`}
                          onClick={() => onRemove(instructor)}
                          disabled={isSaving}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  )
}
